import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { getCachedAtlasImage, prefetchAtlasIcons } from '../../../utils/atlasItemIcon';
import { addQuestShapePath, drawDependencyCurve, questFillColor, questStrokeColor } from '../../../utils/questSnbt/draw';
import {
  buildQuestSpatialHash,
  clampZoom,
  collectRelatedIds,
  fitCameraToBounds,
  QUEST_UNIT_PX,
  questScreenRadius,
  screenToWorld,
  visibleQuestIds,
  worldToScreen,
  zoomAtPoint,
  type Camera,
  type Viewport,
} from '../../../utils/questSnbt/layout';
import { displayQuestTitle, type QuestChapterModel, type QuestNode } from '../../../utils/questSnbt/model';

export interface QuestChapterCanvasHandle {
  fit: () => void;
  focusQuest: (id: string) => void;
  getCamera: () => Camera;
}

export interface QuestChapterCanvasProps {
  model: QuestChapterModel;
  clusterId: string;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  searchQuery?: string;
  className?: string;
}

const MIN_TITLE_ZOOM = 0.82;

export const QuestChapterCanvas = forwardRef<QuestChapterCanvasHandle, QuestChapterCanvasProps>(
  function QuestChapterCanvas({ model, clusterId, selectedId, onSelect, searchQuery = '', className }, ref) {
    const wrapRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const camRef = useRef<Camera>({ x: 0, y: 0, zoom: 1 });
    const viewRef = useRef<Viewport>({ width: 1, height: 1 });
    const hoverRef = useRef<string | null>(null);
    const dragRef = useRef<{
      pointerId: number;
      lastX: number;
      lastY: number;
      moved: boolean;
    } | null>(null);
    const dirtyRef = useRef(true);
    const rafRef = useRef<number | 0>(0);
    const fittedForRef = useRef<string | null>(null);
    const iconTickRef = useRef(0);

    const hash = useMemo(() => buildQuestSpatialHash(model), [model]);
    const searchLower = searchQuery.trim().toLowerCase();
    const searchMatches = useMemo(() => {
      if (!searchLower) return null;
      const ids = new Set<string>();
      for (const q of model.quests) {
        if (displayQuestTitle(q.title).toLowerCase().includes(searchLower) || q.id.toLowerCase().includes(searchLower)) {
          ids.add(q.id);
        }
      }
      return ids;
    }, [model, searchLower]);

    const markDirty = useCallback(() => {
      dirtyRef.current = true;
    }, []);

    const fit = useCallback(() => {
      camRef.current = fitCameraToBounds(model.bounds, viewRef.current);
      markDirty();
    }, [model.bounds, markDirty]);

    const focusQuest = useCallback(
      (id: string) => {
        const q = model.questById.get(id);
        if (!q) return;
        const { width, height } = viewRef.current;
        const zoom = clampZoom(Math.max(camRef.current.zoom, 1.15));
        camRef.current = {
          zoom,
          x: width / 2 - q.x * QUEST_UNIT_PX * zoom,
          y: height / 2 - q.y * QUEST_UNIT_PX * zoom,
        };
        markDirty();
      },
      [model.questById, markDirty],
    );

    useImperativeHandle(
      ref,
      () => ({
        fit,
        focusQuest,
        getCamera: () => camRef.current,
      }),
      [fit, focusQuest],
    );

    useEffect(() => {
      let cancelled = false;
      const onEach = () => {
        if (cancelled) return;
        iconTickRef.current += 1;
        markDirty();
      };
      const visibleFirst = visibleQuestIds(hash, camRef.current, viewRef.current)
        .map((id) => model.questById.get(id)?.iconItemId)
        .filter((id): id is string => !!id);
      const rest = model.iconItemIds.filter((id) => !visibleFirst.includes(id));
      void prefetchAtlasIcons(clusterId, [...visibleFirst, ...rest], 16, onEach);
      return () => {
        cancelled = true;
      };
    }, [clusterId, hash, model, markDirty]);

    useEffect(() => {
      const wrap = wrapRef.current;
      const canvas = canvasRef.current;
      if (!wrap || !canvas) return;

      const resize = () => {
        const rect = wrap.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        viewRef.current = { width: Math.max(1, rect.width), height: Math.max(1, rect.height) };
        canvas.width = Math.max(1, Math.floor(rect.width * dpr));
        canvas.height = Math.max(1, Math.floor(rect.height * dpr));
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;
        if (fittedForRef.current !== model.id) {
          camRef.current = fitCameraToBounds(model.bounds, viewRef.current);
          fittedForRef.current = model.id;
        }
        markDirty();
      };

      resize();
      const ro = new ResizeObserver(resize);
      ro.observe(wrap);
      return () => ro.disconnect();
    }, [model.id, model.bounds, markDirty]);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const onWheelNative = (e: WheelEvent) => {
        e.preventDefault();
        const rect = canvas.getBoundingClientRect();
        const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
        camRef.current = zoomAtPoint(
          camRef.current,
          e.clientX - rect.left,
          e.clientY - rect.top,
          camRef.current.zoom * factor,
        );
        markDirty();
      };
      canvas.addEventListener('wheel', onWheelNative, { passive: false });
      return () => canvas.removeEventListener('wheel', onWheelNative);
    }, [markDirty]);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const draw = () => {
        rafRef.current = requestAnimationFrame(draw);
        if (!dirtyRef.current) return;
        dirtyRef.current = false;

        const dpr = window.devicePixelRatio || 1;
        const vw = viewRef.current.width;
        const vh = viewRef.current.height;
        const cam = camRef.current;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, vw, vh);

        const bg = ctx.createRadialGradient(vw * 0.5, vh * 0.4, 20, vw * 0.5, vh * 0.5, Math.max(vw, vh) * 0.75);
        bg.addColorStop(0, 'rgba(36, 16, 72, 0.92)');
        bg.addColorStop(1, 'rgba(8, 3, 18, 0.96)');
        ctx.fillStyle = bg;
        ctx.fillRect(0, 0, vw, vh);

        drawWorldGrid(ctx, cam, vw, vh);

        const visible = visibleQuestIds(hash, cam, viewRef.current);
        const visibleSet = new Set(visible);
        const related = selectedId ? collectRelatedIds(model, selectedId) : null;
        const hovered = hoverRef.current;

        for (const edge of model.edges) {
          if (!visibleSet.has(edge.from) && !visibleSet.has(edge.to)) continue;
          const from = model.questById.get(edge.from);
          const to = model.questById.get(edge.to);
          if (!from || !to) continue;
          const a = worldToScreen(cam, from.x, from.y);
          const b = worldToScreen(cam, to.x, to.y);
          const hot =
            hovered === edge.from ||
            hovered === edge.to ||
            selectedId === edge.from ||
            selectedId === edge.to;
          drawDependencyCurve(ctx, a.x, a.y, b.x, b.y, hot);
        }

        for (const id of visible) {
          const q = model.questById.get(id);
          if (!q || q.invisible) continue;
          const dimmed = searchMatches != null && !searchMatches.has(q.id);
          drawQuestNode(ctx, q, cam, {
            hovered: hovered === q.id,
            selected: selectedId === q.id,
            related: related?.has(q.id) ?? false,
            dimmed,
            clusterId,
            showTitle: cam.zoom >= MIN_TITLE_ZOOM || selectedId === q.id || hovered === q.id,
          });
        }

        if (hovered) {
          const q = model.questById.get(hovered);
          if (q && cam.zoom < MIN_TITLE_ZOOM) {
            const p = worldToScreen(cam, q.x, q.y);
            const label = displayQuestTitle(q.title);
            ctx.font = '600 12px Inter, system-ui, sans-serif';
            const w = ctx.measureText(label).width;
            const tx = Math.min(vw - w - 16, Math.max(8, p.x - w / 2));
            const ty = p.y - questScreenRadius(q, cam.zoom) - 18;
            ctx.fillStyle = 'rgba(12, 5, 24, 0.88)';
            ctx.fillRect(tx - 6, ty - 12, w + 12, 20);
            ctx.strokeStyle = 'rgba(172, 133, 255, 0.45)';
            ctx.strokeRect(tx - 6, ty - 12, w + 12, 20);
            ctx.fillStyle = 'rgba(255,255,255,0.92)';
            ctx.fillText(label, tx, ty + 3);
          }
        }
      };

      rafRef.current = requestAnimationFrame(draw);
      return () => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
      };
    }, [clusterId, hash, model, searchMatches, selectedId]);

    const hitTest = useCallback(
      (clientX: number, clientY: number): string | null => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const world = screenToWorld(camRef.current, clientX - rect.left, clientY - rect.top);
        return hash.hit(world.x, world.y);
      },
      [hash],
    );

    const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (e.button !== 0) return;
      wrapRef.current?.focus();
      (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
      dragRef.current = { pointerId: e.pointerId, lastX: e.clientX, lastY: e.clientY, moved: false };
    };

    const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
      const drag = dragRef.current;
      if (drag && drag.pointerId === e.pointerId) {
        const dx = e.clientX - drag.lastX;
        const dy = e.clientY - drag.lastY;
        if (Math.abs(dx) + Math.abs(dy) > 2) drag.moved = true;
        drag.lastX = e.clientX;
        drag.lastY = e.clientY;
        camRef.current = { ...camRef.current, x: camRef.current.x + dx, y: camRef.current.y + dy };
        markDirty();
        return;
      }
      const id = hitTest(e.clientX, e.clientY);
      if (id !== hoverRef.current) {
        hoverRef.current = id;
        const canvas = canvasRef.current;
        if (canvas) canvas.style.cursor = id ? 'pointer' : 'grab';
        markDirty();
      }
    };

    const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
      const drag = dragRef.current;
      dragRef.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
      if (drag && !drag.moved) {
        const id = hitTest(e.clientX, e.clientY);
        onSelect(id);
      }
    };

    const onPointerLeave = () => {
      if (hoverRef.current) {
        hoverRef.current = null;
        markDirty();
      }
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      const step = 48;
      if (e.key === 'ArrowLeft') {
        camRef.current = { ...camRef.current, x: camRef.current.x + step };
        markDirty();
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        camRef.current = { ...camRef.current, x: camRef.current.x - step };
        markDirty();
        e.preventDefault();
      } else if (e.key === 'ArrowUp') {
        camRef.current = { ...camRef.current, y: camRef.current.y + step };
        markDirty();
        e.preventDefault();
      } else if (e.key === 'ArrowDown') {
        camRef.current = { ...camRef.current, y: camRef.current.y - step };
        markDirty();
        e.preventDefault();
      } else if (e.key === '+' || e.key === '=') {
        const { width, height } = viewRef.current;
        camRef.current = zoomAtPoint(camRef.current, width / 2, height / 2, camRef.current.zoom * 1.15);
        markDirty();
        e.preventDefault();
      } else if (e.key === '-' || e.key === '_') {
        const { width, height } = viewRef.current;
        camRef.current = zoomAtPoint(camRef.current, width / 2, height / 2, camRef.current.zoom / 1.15);
        markDirty();
        e.preventDefault();
      } else if (e.key === '0' || e.key === 'f' || e.key === 'F') {
        fit();
        e.preventDefault();
      }
    };

    return (
      <div
        ref={wrapRef}
        className={className ?? 'relative w-full h-full min-h-[280px]'}
        tabIndex={0}
        onKeyDown={onKeyDown}
      >
        <canvas
          ref={canvasRef}
          className="block w-full h-full touch-none cursor-grab"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerLeave}
          onContextMenu={(e) => e.preventDefault()}
        />
      </div>
    );
  },
);

function drawWorldGrid(ctx: CanvasRenderingContext2D, cam: Camera, vw: number, vh: number): void {
  if (cam.zoom < 0.45) return;
  const step = cam.zoom >= 1.4 ? 1 : 2;
  const tl = screenToWorld(cam, 0, 0);
  const br = screenToWorld(cam, vw, vh);
  const minX = Math.floor(Math.min(tl.x, br.x) / step) * step;
  const maxX = Math.ceil(Math.max(tl.x, br.x) / step) * step;
  const minY = Math.floor(Math.min(tl.y, br.y) / step) * step;
  const maxY = Math.ceil(Math.max(tl.y, br.y) / step) * step;
  ctx.beginPath();
  ctx.strokeStyle = 'rgba(172, 133, 255, 0.07)';
  ctx.lineWidth = 1;
  for (let x = minX; x <= maxX; x += step) {
    const s = worldToScreen(cam, x, 0);
    ctx.moveTo(s.x, 0);
    ctx.lineTo(s.x, vh);
  }
  for (let y = minY; y <= maxY; y += step) {
    const s = worldToScreen(cam, 0, y);
    ctx.moveTo(0, s.y);
    ctx.lineTo(vw, s.y);
  }
  ctx.stroke();
}

function drawQuestNode(
  ctx: CanvasRenderingContext2D,
  quest: QuestNode,
  cam: Camera,
  opts: {
    hovered: boolean;
    selected: boolean;
    related: boolean;
    dimmed: boolean;
    clusterId: string;
    showTitle: boolean;
  },
): void {
  const p = worldToScreen(cam, quest.x, quest.y);
  const r = questScreenRadius(quest, cam.zoom);
  ctx.save();
  if (opts.dimmed) ctx.globalAlpha = 0.22;

  if (opts.selected || opts.hovered) {
    ctx.beginPath();
    ctx.arc(p.x, p.y, r + 7, 0, Math.PI * 2);
    ctx.fillStyle = opts.selected ? 'rgba(125, 69, 255, 0.22)' : 'rgba(251, 191, 36, 0.14)';
    ctx.fill();
  }

  addQuestShapePath(ctx, p.x, p.y, r, quest.shape);
  ctx.fillStyle = questFillColor(quest, opts.hovered, opts.selected, opts.related);
  ctx.fill();
  ctx.lineWidth = opts.selected ? 2.6 : 1.8;
  ctx.strokeStyle = questStrokeColor(quest, opts.hovered, opts.selected);
  ctx.stroke();

  const img = quest.iconItemId ? getCachedAtlasImage(opts.clusterId, quest.iconItemId) : null;
  const iconR = r * 0.72;
  if (img && img.complete && img.naturalWidth > 0) {
    ctx.drawImage(img, p.x - iconR, p.y - iconR, iconR * 2, iconR * 2);
  } else if (quest.iconItemId) {
    ctx.fillStyle = 'rgba(255,255,255,0.18)';
    ctx.beginPath();
    ctx.arc(p.x, p.y, iconR * 0.35, 0, Math.PI * 2);
    ctx.fill();
  }

  if (opts.showTitle) {
    const label = displayQuestTitle(quest.title);
    ctx.font = `${opts.selected ? 600 : 500} ${Math.max(10, Math.min(13, 9 + cam.zoom * 3))}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(8, 3, 18, 0.85)';
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.strokeText(label, p.x, p.y + r + 4);
    ctx.fillText(label, p.x, p.y + r + 4);
    ctx.textAlign = 'start';
    ctx.textBaseline = 'alphabetic';
  }

  ctx.restore();
}
